import { useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SummaryTabEditor from "./SummaryTabEditor";
import { formatTickers } from "./functions";
import {
  COMPANY_TYPE_OPTIONS,
  CORPORATE_STRUCTURE_OPTIONS,
  COMPANY_LIFECYCLE_OPTIONS,
  COMPANY_LIFECYCLE_STAGE_OPTIONS,
} from "../../constants/companyFields";
import { formatChoiceLabel, mapChoiceByOptions } from "./summaryTabUtils";
import {
  METHODOLOGY_NOTE,
  formatScore,
  formatScoreDate,
  getPillarPresentation,
  getScoreProgress,
  getScoreTone,
  normalizeCompanyScorePayload,
} from "../../utils/scorePresentation";

const CARD_SX = {
  borderRadius: 2,
  border: "1px solid rgba(34,35,37,0.10)",
  boxShadow: "0 2px 10px rgba(15,23,42,0.04)",
  background: "rgba(255,255,255,0.78)",
  transition: "none !important",
  "&:hover": {
    boxShadow: "0 2px 10px rgba(15,23,42,0.04) !important",
    background: "rgba(255,255,255,0.78) !important",
  },
};

const FINANCIAL_FIELDS = [
  ["income", "sales", "Revenue", "currency"],
  ["income", "ebitda", "EBITDA", "currency"],
  ["cash_flow", "free_cash_flow", "Free Cash Flow", "currency"],
  ["balance_sheet", "assets_current_assets_cash_and_cash_equivalents", "Cash & Equivalents", "currency"],
  ["income", "ebitda_margin", "EBITDA Margin", "percent"],
  ["cash_flow", "free_cash_flow_margin", "FCF Margin", "percent"],
  ["balance_sheet", "net_debt_to_ebitda", "Net Debt / EBITDA", "multiple"],
  ["balance_sheet", "current_ratio", "Current Ratio", "multiple"],
  ["income", "return_on_equity", "ROE", "percent"],
];

function SummaryTabDisplay({ company, headerAction, mode, onNavigateTab }) {
  const profile = company?.profile || {};
  const scores = normalizeCompanyScorePayload(company?.scores);
  const financials = company?.financials || null;
  const statistics = company?.statistics || null;
  const assetsPayload = company?.public_assets || null;
  const lifecycle = formatChoiceLabel(
    mapChoiceByOptions(profile?.company_lifecycle, COMPANY_LIFECYCLE_OPTIONS),
    COMPANY_LIFECYCLE_OPTIONS
  );
  const lifecycleStage = formatChoiceLabel(
    mapChoiceByOptions(profile?.company_lifecycle_stage, COMPANY_LIFECYCLE_STAGE_OPTIONS),
    COMPANY_LIFECYCLE_STAGE_OPTIONS
  );
  const commodities = normalizeCommodities(profile?.commodities);
  const headquarters = [profile?.hq_city, profile?.hq_country].filter(Boolean).join(", ");
  const tickers = formatTickers(profile?.tickers_exchanges);
  const financialSnapshot = useMemo(() => buildFinancialSnapshot(financials), [financials]);
  const statisticsSnapshot = useMemo(() => buildStatisticsSnapshot(statistics), [statistics]);
  const assetSnapshot = useMemo(() => buildAssetSnapshot(assetsPayload), [assetsPayload]);
  const atAGlanceItems = [
    lifecycle && { icon: FactoryOutlinedIcon, label: "Lifecycle", value: lifecycle },
    statisticsSnapshot.marketCap && { icon: PaidOutlinedIcon, label: "Market Cap", value: statisticsSnapshot.marketCap },
    statisticsSnapshot.insiderOwnership && { icon: GroupsOutlinedIcon, label: "Insider Ownership", value: statisticsSnapshot.insiderOwnership },
    statisticsSnapshot.institutionalOwnership && { icon: AccountBalanceOutlinedIcon, label: "Institutional Ownership", value: statisticsSnapshot.institutionalOwnership },
    commodities[0] && { icon: Inventory2OutlinedIcon, label: "Primary Commodity", value: commodities[0] },
    headquarters && { icon: BusinessOutlinedIcon, label: "Headquarters", value: headquarters },
    assetSnapshot.assetCount !== null && { icon: HubOutlinedIcon, label: "Assets", value: assetSnapshot.assetCount },
    assetSnapshot.countryCount !== null && { icon: PublicOutlinedIcon, label: "Countries", value: assetSnapshot.countryCount },
    tickers && tickers !== "-" && { icon: AccountBalanceOutlinedIcon, label: "Exchanges / Tickers", value: tickers },
  ].filter(Boolean);

  if (mode === "profile") {
    return (
      <CompanyProfileDetails
        company={company}
        profile={profile}
        lifecycle={lifecycle}
        lifecycleStage={lifecycleStage}
        tickers={tickers}
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Stack spacing={2.5}>
        {headerAction ? <Box sx={{ display: "flex", justifyContent: "flex-end" }}>{headerAction}</Box> : null}
        <CompanyScoreOverviewCard scores={scores} />
        <AtAGlanceCard items={atAGlanceItems} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <FinancialSnapshotCard snapshot={financialSnapshot} onNavigateTab={onNavigateTab} />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <AssetsSnapshotCard snapshot={assetSnapshot} onNavigateTab={onNavigateTab} />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <ScoreBreakdownCard scores={scores} />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <DataAvailabilityCard
              profile={profile}
              financialSnapshot={financialSnapshot}
              assetSnapshot={assetSnapshot}
              scores={scores}
            />
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}

function CompanyProfileDetails({ company, profile, lifecycle, lifecycleStage, tickers }) {
  const managementPerson = (company?.management_people || [])[0] || null;
  const companyType = formatChoiceLabel(
    mapChoiceByOptions(profile?.company_type, COMPANY_TYPE_OPTIONS),
    COMPANY_TYPE_OPTIONS
  );
  const corporateStructure = formatChoiceLabel(
    mapChoiceByOptions(profile?.corporate_structure, CORPORATE_STRUCTURE_OPTIONS),
    CORPORATE_STRUCTURE_OPTIONS
  );
  const mediaLinks = [
    ["Recent News", profile?.recent_news_link],
    ["Corporate Presentation", profile?.corporate_presentation],
    ["NRS Company Spotlight", profile?.nrs_company_spotlight_link],
    ["Featured Video", profile?.featured_video_link],
  ].filter(([, value]) => Boolean(value));
  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Stack spacing={3}>
        <Box>
          <ProfileSectionTitle title="Company Description" />
          <Box
            sx={{
              mt: 1.5,
              px: { xs: 2, md: 2 },
              py: { xs: 1.75, md: 2 },
              borderRadius: 2,
              backgroundColor: "rgba(34,35,37,0.04)",
            }}
          >
            <Typography sx={{ fontSize: 16, whiteSpace: "pre-line", lineHeight: 1.7 }}>
              {profile?.corporate_description || "No description available yet."}
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Box>
          <ProfileSectionTitle title="Company Snapshot" />
          <Grid container spacing={2} sx={{ mt: 1.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={1.25}>
                <ProfileInfoRow label="Company Name" value={company?.name} />
                <ProfileInfoRow
                  label="Website"
                  value={profile?.website_url}
                  href={profile?.website_url ? ensureExternalUrl(profile.website_url) : ""}
                />
                <ProfileInfoRow label="Company Type" value={companyType} />
                <ProfileInfoRow label="Lifecycle" value={lifecycle} />
                <ProfileInfoRow label="Lifecycle Stage" value={lifecycleStage} />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={1.25}>
                <ProfileInfoRow label="Contact Email" value={profile?.contact_email} href={profile?.contact_email ? `mailto:${profile.contact_email}` : ""} />
                <ProfileInfoRow label="Contact Phone" value={profile?.contact_phone} />
                <ProfileInfoRow label="Year Founded" value={profile?.year_founded} />
                <ProfileInfoRow label="Corporate Structure" value={corporateStructure} />
                <ProfileInfoRow label="Tickers" value={tickers} />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {managementPerson ? (
          <>
            <Divider />
            <Box>
              <ProfileSectionTitle title="Key Executive Information" />
              <Grid container spacing={2} sx={{ mt: 1.5 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ProfileInfoRow label="Name" value={managementPerson.executive_name} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ProfileInfoRow label="Title" value={managementPerson.role_title} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      px: { xs: 2, md: 2 },
                      py: { xs: 1.75, md: 2 },
                      borderRadius: 2,
                      backgroundColor: "rgba(34,35,37,0.04)",
                      minHeight: "100%",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                      Background Summary
                    </Typography>
                    <Typography sx={{ fontSize: 15, whiteSpace: "pre-line", lineHeight: 1.6 }}>
                      {managementPerson.background_summary || "No background summary available yet."}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        ) : null}

        {mediaLinks.length ? (
          <>
            <Divider />
            <Box>
              <ProfileSectionTitle title="Featured Media" />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                {mediaLinks.map(([label, href]) => (
                  <Button key={label} href={ensureExternalUrl(href)} target="_blank" rel="noopener noreferrer" variant="outlined" size="small">
                    {label}
                  </Button>
                ))}
              </Stack>
            </Box>
          </>
        ) : null}
      </Stack>
    </Box>
  );
}

function CompanyScoreOverviewCard({ scores }) {
  const available = scores.status === "available" && scores.overall_score !== null;
  const tone = getScoreTone(scores.overall_score);
  const asOf = formatScoreDate(scores.calculated_at);
  return (
    <Paper sx={{ ...CARD_SX, p: { xs: 2, md: 3 } }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={1.5}>
            <SectionTitle title="Overall Investment Score" />
            {available ? (
              <>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography sx={{ fontSize: { xs: 64, md: 82 }, lineHeight: 0.95, fontWeight: 800, color: tone.color }}>
                    {formatScore(scores.overall_score)}
                  </Typography>
                  <Typography variant="h5" color="text.secondary">/100</Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={getScoreProgress(scores.overall_score)}
                  aria-label={`Overall investment score: ${formatScore(scores.overall_score)} out of 100`}
                  sx={{
                    width: { xs: "100%", sm: 260 },
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: "rgba(34,35,37,0.10)",
                    "& .MuiLinearProgress-bar": { backgroundColor: tone.color, borderRadius: 999 },
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {asOf ? `As of ${asOf}` : "Score date unavailable"}
                  {scores.model_version ? ` - Model: ${scores.model_version}` : ""}
                </Typography>
              </>
            ) : (
              <Box sx={{ p: 2, borderRadius: 2, backgroundColor: "rgba(34,35,37,0.04)" }}>
                <Typography variant="h5" fontWeight={800}>Score unavailable</Typography>
                <Typography variant="body2" color="text.secondary">
                  There is not yet enough data to calculate this company's investment score.
                </Typography>
              </Box>
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={1.5}>
            <SectionTitle title="Top-Level Score Drivers" />
            {scores.pillars.length ? (
              scores.pillars.map((pillar) => <PillarScoreRow key={pillar.code} pillar={pillar} />)
            ) : (
              <Typography variant="body2" color="text.secondary">Pillar scores unavailable.</Typography>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

function PillarScoreRow({ pillar }) {
  const presentation = getPillarPresentation(pillar.code);
  const Icon = presentation.icon;
  const tone = getScoreTone(pillar.score);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "44px minmax(0, 1fr) 70px", gap: 1.5, alignItems: "center" }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 1.5, display: "grid", placeItems: "center", color: tone.color, backgroundColor: tone.background }}>
        <Icon fontSize="small" aria-hidden />
      </Box>
      <Box>
        <Typography variant="body2" fontWeight={800}>{pillar.name}</Typography>
        {presentation.description ? <Typography variant="caption" color="text.secondary">{presentation.description}</Typography> : null}
        <LinearProgress
          variant="determinate"
          value={getScoreProgress(pillar.score)}
          aria-label={`${pillar.name} score: ${formatScore(pillar.score)} out of 100`}
          sx={{ mt: 0.75, height: 6, borderRadius: 999, backgroundColor: "rgba(34,35,37,0.10)", "& .MuiLinearProgress-bar": { backgroundColor: tone.color, borderRadius: 999 } }}
        />
      </Box>
      <Typography variant="body2" fontWeight={800} sx={{ textAlign: "right" }}>
        {formatScore(pillar.score)} <Typography component="span" variant="caption" color="text.secondary">/100</Typography>
      </Typography>
    </Box>
  );
}

function AtAGlanceCard({ items }) {
  return (
    <SummaryCard title="At a Glance">
      {items.length ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(auto-fit, minmax(120px, 1fr))" }, gap: 1.5 }}>
          {items.map((item) => <SummaryStatItem key={item.label} {...item} />)}
        </Box>
      ) : (
        <EmptyText>Company snapshot unavailable.</EmptyText>
      )}
    </SummaryCard>
  );
}

function FinancialSnapshotCard({ snapshot, onNavigateTab }) {
  return (
    <SummaryCard title="Financial Snapshot">
      {snapshot.hasData ? (
        <>
          <Typography variant="caption" color="text.secondary">
            {snapshot.periodLabel ? `${snapshot.periodLabel}` : "Current financial data"}
            {snapshot.currency ? ` - Currency: ${snapshot.currency}` : ""}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 1.5, mt: 2 }}>
            {snapshot.metrics.map((metric) => (
              <Box key={metric.label}>
                <Typography variant="caption" color="text.secondary">{metric.label}</Typography>
                <Typography variant="body1" fontWeight={800}>{metric.value}</Typography>
              </Box>
            ))}
          </Box>
          <Button endIcon={<ArrowForwardIcon />} size="small" sx={{ mt: 2, px: 0 }} onClick={() => onNavigateTab?.("financials")}>
            View full financials
          </Button>
        </>
      ) : (
        <EmptyText>Financial snapshot unavailable.</EmptyText>
      )}
    </SummaryCard>
  );
}

function AssetsSnapshotCard({ snapshot, onNavigateTab }) {
  return (
    <SummaryCard title="Assets Snapshot">
      {snapshot.hasData ? (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
            <MiniMetric label="Assets / Projects" value={snapshot.assetCount} />
            <MiniMetric label="Countries" value={snapshot.countryCount} />
            <MiniMetric label="Commodities" value={snapshot.commodityCount} />
            <MiniMetric label="Producing Assets" value={snapshot.producingCount} />
          </Box>
          {snapshot.resourceRows.length ? (
            <TableContainer sx={{ mt: 2, maxWidth: "100%" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Commodity</TableCell>
                    <TableCell align="right">P&P</TableCell>
                    <TableCell align="right">M&I</TableCell>
                    <TableCell align="right">Inferred</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {snapshot.resourceRows.slice(0, 4).map((row) => (
                    <TableRow key={row.commodity}>
                      <TableCell>{row.commodity}</TableCell>
                      <TableCell align="right">{formatResourceValue((row.proven || 0) + (row.probable || 0), row.unit)}</TableCell>
                      <TableCell align="right">{formatResourceValue((row.measured || 0) + (row.indicated || 0), row.unit)}</TableCell>
                      <TableCell align="right">{formatResourceValue(row.inferred, row.unit)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
          <Button endIcon={<ArrowForwardIcon />} size="small" sx={{ mt: 2, px: 0 }} onClick={() => onNavigateTab?.("assets")}>
            View all assets
          </Button>
        </>
      ) : (
        <EmptyText>Asset snapshot unavailable.</EmptyText>
      )}
    </SummaryCard>
  );
}

function ScoreBreakdownCard({ scores }) {
  return (
    <SummaryCard title="Score Breakdown & Methodology">
      {scores.status === "available" && scores.pillars.length ? (
        <Grid container spacing={2}>
          {scores.pillars.map((pillar) => (
            <Grid key={pillar.code} size={{ xs: 12, md: 6 }}>
              <Stack spacing={1}>
                <PillarScoreRow pillar={pillar} />
                {Array.isArray(pillar.groups) && pillar.groups.length ? (
                  pillar.groups.map((group) => <GroupScoreRow key={group.code} group={group} />)
                ) : (
                  <Typography variant="caption" color="text.secondary">Detailed score groups coming soon.</Typography>
                )}
              </Stack>
            </Grid>
          ))}
        </Grid>
      ) : (
        <EmptyText>Score breakdown unavailable.</EmptyText>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        {METHODOLOGY_NOTE}
      </Typography>
    </SummaryCard>
  );
}

function GroupScoreRow({ group }) {
  const tone = getScoreTone(group.score);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 58px", gap: 1, alignItems: "center" }}>
      <Box>
        <Typography variant="caption">{group.name}</Typography>
        <LinearProgress
          variant="determinate"
          value={getScoreProgress(group.score)}
          aria-label={`${group.name} score: ${formatScore(group.score)} out of 100`}
          sx={{ height: 5, borderRadius: 999, backgroundColor: "rgba(34,35,37,0.10)", "& .MuiLinearProgress-bar": { backgroundColor: tone.color, borderRadius: 999 } }}
        />
      </Box>
      <Typography variant="caption" fontWeight={800} textAlign="right">{formatScore(group.score)} /100</Typography>
    </Box>
  );
}

function DataAvailabilityCard({ profile, financialSnapshot, assetSnapshot, scores }) {
  const rows = [
    ["Company Profile", hasProfile(profile)],
    ["Financial Snapshot", financialSnapshot.hasData],
    ["Assets and Projects", assetSnapshot.hasData],
    ["Resource Reports", assetSnapshot.resourceRows.length > 0],
    ["Scores", scores.status === "available"],
  ];
  return (
    <SummaryCard title="Data Availability">
      <Stack spacing={1.25}>
        {rows.map(([label, available]) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="body2">{label}</Typography>
            <Chip
              size="small"
              icon={available ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
              label={available ? "Available" : "Unavailable"}
              color={available ? "success" : "default"}
              variant={available ? "outlined" : "filled"}
            />
          </Box>
        ))}
      </Stack>
    </SummaryCard>
  );
}

function SummaryCard({ title, children, titleVariant = "summary" }) {
  return (
    <Paper sx={{ ...CARD_SX, p: { xs: 2, md: 2.5 }, height: "100%" }}>
      {titleVariant === "profile" ? <ProfileSectionTitle title={title} /> : <SectionTitle title={title} />}
      <Box sx={{ mt: 1.5 }}>{children}</Box>
    </Paper>
  );
}

function SectionTitle({ title }) {
  return (
    <Typography variant="subtitle2" fontWeight={900} sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
      {title}
    </Typography>
  );
}

function ProfileSectionTitle({ title }) {
  return (
    <Typography variant="h6" fontWeight={700}>
      {title}
    </Typography>
  );
}

function SummaryStatItem({ icon: Icon, label, value }) {
  return (
    <Box sx={{ textAlign: "center", px: 1, py: 1.5, borderRight: { md: "1px solid rgba(34,35,37,0.10)" } }}>
      <Icon sx={{ color: "#C48600", mb: 0.75 }} aria-hidden />
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" fontWeight={800} sx={{ wordBreak: "break-word" }}>{value}</Typography>
    </Box>
  );
}

function MiniMetric({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body1" fontWeight={800}>{value ?? "-"}</Typography>
    </Box>
  );
}

function ProfileInfoRow({ label, value, href }) {
  const display = value || "-";
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {href && value ? (
        <Typography
          component="a"
          href={href}
          target={href.startsWith("mailto:") ? undefined : "_blank"}
          rel={href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
          variant="body2"
          sx={{ color: "primary.main", textDecoration: "none", textAlign: "right", wordBreak: "break-word" }}
        >
          {display}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ textAlign: "right", wordBreak: "break-word" }}>{display}</Typography>
      )}
    </Box>
  );
}

function EmptyText({ children }) {
  return <Typography variant="body2" color="text.secondary">{children}</Typography>;
}

function ensureExternalUrl(value) {
  if (!value) return "";
  return String(value).includes("://") ? value : `https://${value}`;
}

function buildFinancialSnapshot(financials) {
  const granularity = financials?.statements?.income?.ttm?.periods?.length ? "ttm" : "annual";
  const period = financials?.statements?.income?.[granularity]?.periods?.[0] || null;
  const currency = financials?.has_usd ? "USD" : financials?.currency || "";
  const metrics = FINANCIAL_FIELDS.map(([statement, code, label, format]) => {
    const value = getFinancialValue(financials, statement, granularity, code);
    return { label, value: formatFinancialValue(value, format, currency) };
  }).filter((item) => item.value !== "-");
  const marketCap = formatFinancialValue(getFinancialValue(financials, "balance_sheet", granularity, "market_cap"), "currency", currency);
  return {
    hasData: metrics.length > 0,
    metrics,
    periodLabel: period?.label || (period?.end_date ? `Period ending ${formatDate(period.end_date)}` : ""),
    currency,
    marketCap: marketCap !== "-" ? marketCap : null,
  };
}

function buildStatisticsSnapshot(statistics) {
  const currency = statistics?.currency || "USD";
  return {
    marketCap: formatCompactCurrency(statistics?.market_capitalization, currency),
    insiderOwnership: formatPercent(statistics?.percent_held_by_insiders),
    institutionalOwnership: formatPercent(statistics?.percent_held_by_institutions),
  };
}

function getFinancialValue(financials, statement, granularity, code) {
  const block = financials?.statements?.[statement]?.[granularity] || {};
  const byType = block.data_by_value_type || {};
  const derived = byType.derived?.[code] || [];
  const usd = byType.usd?.[code] || [];
  const reported = byType.reported?.[code] || [];
  return derived[0] ?? usd[0] ?? reported[0] ?? null;
}

function buildAssetSnapshot(payload) {
  const assets = Array.isArray(payload?.assets) ? payload.assets : [];
  const jurisdictions = new Set(assets.map((asset) => asset.jurisdiction).filter(Boolean));
  const commodities = new Set();
  let producingCount = 0;
  assets.forEach((asset) => {
    if (String(asset.lifecycle_code || "").includes("production") || String(asset.stage || "").toLowerCase().includes("produc")) {
      producingCount += 1;
    }
    (asset.resources || []).forEach((resource) => {
      (resource.commodities || []).forEach((commodity) => commodities.add(commodity));
    });
  });
  const resourceRows = payload?.company_summary?.resource_estimates || [];
  resourceRows.forEach((row) => row.commodity && commodities.add(row.commodity));
  return {
    hasData: assets.length > 0 || resourceRows.length > 0,
    assetCount: assets.length || null,
    countryCount: jurisdictions.size || null,
    commodityCount: commodities.size || null,
    producingCount,
    resourceRows,
  };
}

function normalizeCommodities(value) {
  if (!value) return [];
  const list = Array.isArray(value) ? value : String(value).split(",");
  return list
    .map((item) => {
      if (typeof item === "string") return item.trim();
      return item?.commodity || item?.name || item?.value || "";
    })
    .filter(Boolean);
}

function formatFinancialValue(value, format, currency) {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  if (format === "currency") return formatCompactCurrency(numeric, currency);
  if (format === "percent") return `${(numeric * 100).toFixed(1)}%`;
  if (format === "multiple") return `${numeric.toFixed(2)}x`;
  return numeric.toLocaleString();
}

function formatCompactCurrency(value, currency = "USD") {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      notation: "compact",
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    return `${currency || "USD"} ${numeric.toLocaleString()}`;
  }
}

function formatPercent(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return `${(numeric * 100).toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })}%`;
}

function formatResourceValue(value, unit) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) return "-";
  return `${numeric.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${unit || ""}`.trim();
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
}

function hasProfile(profile) {
  return Boolean(profile?.company_lifecycle || profile?.hq_country || profile?.commodities);
}

export default function SummaryTab({
  mode = "public",
  company,
  canEditCompany,
  onCompanyUpdated,
  onNavigateTab,
}) {
  if (mode === "manage") {
    return (
      <SummaryTabEditor
        company={company}
        canEditCompany={canEditCompany}
        onCompanyUpdated={onCompanyUpdated}
        renderDisplay={({ headerAction }) => (
          <SummaryTabDisplay company={company} headerAction={headerAction} mode="profile" onNavigateTab={onNavigateTab} />
        )}
      />
    );
  }

  return <SummaryTabDisplay company={company} mode={mode} onNavigateTab={onNavigateTab} />;
}
